import React, { useCallback, useContext, useEffect, useState } from 'react';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import useFormikHandler from 'screens/Integrations/hooks/useFormikHandler';
import SubmissionFooter from 'screens/Integrations/components/SubmissionFooter';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import events from 'utilities/amplitude/events';
import {
  getCorrectUnitName,
  matchCorrectionsWithMeasurementUnits
} from 'helpers/unitsAbbreviation';
import useProductSearch from 'components/ProductSideBar/helpers/useProductSearch';
import useProduct from 'hooks/useProduct';

import { AgVendMatchContext } from '../context';
import { AgVendContext } from '../../context/Provider';
import {
  findProductByCwfId,
  findProductMatch,
  getProductsToPropagateUnitChange,
  isValidUnit
} from '../helpers';
import AgVendUnitList from '../components/UnitList';

const AgVendUnitsMatch = () => {
  const [productsUnitMatches, setProductsUnitMatches] = useState([]);
  const [unMatchedCount, setUnMatchedCount] = useState(0);
  const [isLoadingProductsData, setIsLoadingProductsData] = useState(true);
  const [cwfProducts, setCwfProducts] = useState([]);
  const { triggerAnalyticsEvent } = useSubmissionAnalytics();

  const { selectedInvoices, excludedProductsIds } = useFormikHandler();
  const {
    getProduct,
    getProductUnits,
    getMasterListProductUnits
  } = useProduct();
  const { searchProductById } = useProductSearch();

  const { releaseStepForward } = useContext(AgVendContext);

  const {
    isLoading,
    state: { unitsMatches, matches },
    updateUnitMatches,
    submitUnitsUpdates,
    propagateUnitChange
  } = useContext(AgVendMatchContext);

  const onNextHandler = async () => {
    await submitUnitsUpdates();
    triggerAnalyticsEvent(
      events.epic.Integrations.IncomingInvoice.invoiceMatchUnits,
      {
        vendor: INTEGRATIONS.agVend
      }
    );
  };

  // This should bring the cwf products data from matches
  useEffect(() => {
    if (!matches?.length) {
      return;
    }

    async function fetchProducts(ids) {
      const products = await Promise.all(
        ids.map(async id => {
          const cwfProduct = await searchProductById(id);
          if (cwfProduct) {
            const units = await getMasterListProductUnits(id);
            return { ...cwfProduct, units };
          }
          const customProduct = await getProduct(id);
          const units = await getProductUnits(id);
          return {
            ...customProduct,
            id: customProduct?.productId,
            name: customProduct?.productName,
            units
          };
        })
      );
      setCwfProducts(old => [...old, ...products]);
      setIsLoadingProductsData(false);
    }

    const productsToFetch = [
      ...new Map(
        matches
          ?.filter(match => match.type === 'Product')
          ?.map(match => [match.cwfEntityId, match.cwfEntityId])
      ).values()
    ];

    if (productsToFetch?.length) {
      fetchProducts(productsToFetch);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  useEffect(() => {
    if (!cwfProducts?.length) {
      return;
    }
    const unitMatches = selectedInvoices
      ?.map(invoice => ({
        ...invoice,
        products: invoice.products
          ?.filter(product => !excludedProductsIds?.includes(product.id))
          ?.map(product => {
            const cwfId = findProductMatch(matches, product)?.cwfEntityId;
            const cwfProduct = findProductByCwfId(cwfProducts, cwfId);
            const unitMatched = isValidUnit(
              getCorrectUnitName(product.totalQuantityUnit),
              cwfProduct.units
            );
            return {
              ...product,
              cwfProduct,
              unitMatched
            };
          })
      }))
      ?.filter(invoice => invoice.products?.length);

    setProductsUnitMatches(unitMatches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoices, cwfProducts]);

  const onChangeUnitQuantity = useCallback(
    (invoiceId, productId, vendorOrRetailerId, unit, quantity) => {
      propagateUnitChange(
        getProductsToPropagateUnitChange(
          unit,
          productsUnitMatches,
          productId,
          unitsMatches,
          vendorOrRetailerId
        ),
        unit
      );

      if (!unit || !quantity) {
        return;
      }
      const measurementUnit = matchCorrectionsWithMeasurementUnits(unit);
      const updatedMatches = unitsMatches ? [...unitsMatches] : [];

      const invoiceIndex = updatedMatches.findIndex(
        invoice => invoice.id === invoiceId
      );

      if (invoiceIndex === -1) {
        // If the invoice does not exist, add it
        const newMatches = [
          ...updatedMatches,
          {
            id: invoiceId,
            productsUnits: [
              { id: productId, unit: measurementUnit, value: quantity }
            ]
          }
        ];
        updateUnitMatches(newMatches);
        return;
      }

      updatedMatches[invoiceIndex] = {
        ...updatedMatches[invoiceIndex],
        productsUnits: updatedMatches[invoiceIndex].productsUnits.some(
          p => p.id === productId
        )
          ? updatedMatches[invoiceIndex].productsUnits.map(p =>
              p.id === productId
                ? { ...p, unit: measurementUnit, value: quantity }
                : p
            )
          : [
              ...updatedMatches[invoiceIndex].productsUnits,
              { id: productId, unit: measurementUnit, value: quantity }
            ]
      };
      updateUnitMatches(updatedMatches);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsMatches, productsUnitMatches]
  );

  useEffect(() => {
    if (!productsUnitMatches?.length) {
      return;
    }
    setUnMatchedCount(
      [...unitsMatches, ...productsUnitMatches]?.reduce((acc, inv) => {
        if (inv.productsUnits?.length) {
          return acc - inv.productsUnits.length;
        }

        return (
          acc +
          inv.products?.reduce((accInternal, product) => {
            return accInternal + (product.unitMatched ? 0 : 1);
          }, 0)
        );
      }, 0)
    );
  }, [productsUnitMatches, unitsMatches]);

  useEffect(() => {
    if (unMatchedCount === 0) {
      releaseStepForward();
    }
  }, [unMatchedCount, releaseStepForward]);

  const nextDisabled = isLoading || isLoadingProductsData || unMatchedCount > 0;

  return (
    <>
      <AgVendUnitList
        invoices={productsUnitMatches}
        onChangeUnit={onChangeUnitQuantity}
        unMatchedCount={unMatchedCount}
        isLoading={isLoading || isLoadingProductsData}
      />
      <SubmissionFooter
        onNext={onNextHandler}
        loading={isLoading}
        nextLabel="Next: Review & Import"
        nextDisabled={nextDisabled}
        integrationType={INTEGRATIONS.agVend}
        isBottomFixed
        isLastStep={false}
        context={AgVendContext}
      />
    </>
  );
};

export default AgVendUnitsMatch;
